import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  XCircle,
  ChevronDown,
  Eye,
  Pencil,
  Trash2,
} from "lucide-react";

interface ProjectItem {
  id: string;
  stt: number;
  function: string;
  projectName: string;
  managementLevel: string;
  leadUnit: string;
  projectLeader: string;
  startTime: string;
  endTime: string;
  acceptanceTime: string;
  budget: string;
  classification: string;
  status: string;
}

const mockData: ProjectItem[] = [
  {
    id: "1",
    stt: 1,
    function: "- Chọn -",
    projectName: "Nghiên cứu ứng dụng AI trong quản lý tài nguyên nước",
    managementLevel: "Cấp Viện",
    leadUnit: "Viện Khoa học Thủy lợi Miền Trung và Tây Nguyên",
    projectLeader: "PGS.TS Nguyễn Văn Minh",
    startTime: "03/2020",
    endTime: "02/2021",
    acceptanceTime: "",
    budget: "250.5",
    classification: "Không đạt",
    status: "Nhiệm vụ chưa hoàn thành",
  },
  {
    id: "2",
    stt: 2,
    function: "- Chọn -",
    projectName: "Phát triển hệ thống cảnh báo lũ sớm",
    managementLevel: "Cấp Quốc gia",
    leadUnit: "Viện Khoa học Thủy lợi Miền Trung và Tây Nguyên",
    projectLeader: "GS.TS Lê Thị Thanh",
    startTime: "06/2019",
    endTime: "05/2020",
    acceptanceTime: "",
    budget: "180.2",
    classification: "Chưa đánh giá",
    status: "Nhiệm vụ chưa hoàn thành",
  },
  {
    id: "3",
    stt: 3,
    function: "- Chọn -",
    projectName: "Nghiên cứu tác động biến đổi khí hậu",
    managementLevel: "Cấp Bộ",
    leadUnit: "Viện Khoa học Thủy lợi Miền Trung và Tây Nguyên",
    projectLeader: "TS. Trần Văn Hùng",
    startTime: "01/2021",
    endTime: "12/2021",
    acceptanceTime: "",
    budget: "320.8",
    classification: "Tạm dừng",
    status: "Nhiệm vụ chưa hoàn thành",
  },
];

const IncompleteProjects: React.FC = () => {
  const navigate = useNavigate();
  const [openMenuProjectId, setOpenMenuProjectId] = useState<string | null>(
    null
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null);
  const [menuPlacement, setMenuPlacement] = useState<"top" | "bottom">(
    "bottom"
  );
  const [buttonPosition, setButtonPosition] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuProjectId) {
        const target = event.target as HTMLElement;
        if (
          !target.closest("[data-project-id]") &&
          !target.closest(".menu-container")
        ) {
          setOpenMenuProjectId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openMenuProjectId) {
        setOpenMenuProjectId(null);
      }
    };

    if (openMenuProjectId) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuProjectId, buttonPosition]);

  const handleDeleteConfirm = () => {
    if (deleteProjectId) {
      console.log("Deleting project:", deleteProjectId);
      setShowDeleteConfirm(false);
      setDeleteProjectId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteProjectId(null);
  };

  return (
    <PageShell title="Đề tài không hoàn thành">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Đề tài không hoàn thành
          </h1>
          <button
            onClick={() => navigate("/add-new-project")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Dự án</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm đề tài không hoàn thành"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - 6 filter dropdowns */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Đơn vị chủ trì -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Chọn cấp -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Dạng đề tài -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Lĩnh vực -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Chủ nhiệm -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Xếp loại -</option>
              </select>
            </div>

            {/* Row 3 - 6 filter dropdowns */}
            <div className="grid grid-cols-6 gap-4">
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Thư ký -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Lọc theo -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Từ năm -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white">
                <option>- Đến năm -</option>
              </select>
              <div></div>
              <div></div>
            </div>

            {/* Row 4 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    STT
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Tên nhiệm vụ
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Cấp quản lý
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Đơn vị chủ trì
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Chủ nhiệm nhiệm vụ
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Thời gian bắt đầu
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Thời gian kết thúc
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Thời gian nghiệm thu
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Kinh phí (triệu đồng)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                    Xếp loại
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.stt}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 border-r border-gray-200">
                      <div className="mb-1">
                        <div className="font-medium text-[#337ab7] text-sm leading-tight">
                          {item.projectName} »
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">{item.status}</div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.managementLevel}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      <div className="leading-tight text-sm max-w-[200px]">
                        {item.leadUnit}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      <div className="leading-tight text-sm max-w-[150px]">
                        {item.projectLeader}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.startTime}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.endTime}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      {item.acceptanceTime || (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 text-right border-r border-gray-200">
                      {item.budget ? (
                        <span className="text-red-600 font-semibold text-sm">
                          {item.budget}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 border-r border-gray-200">
                      <div className="flex items-center space-x-2">
                        {item.classification === "Đạt" ? (
                          <>
                            <XCircle className="h-4 w-4 text-green-600" />
                            <span className="text-green-600 font-medium text-sm">
                              {item.classification}
                            </span>
                          </>
                        ) : item.classification === "Không đạt" ? (
                          <>
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-red-600 font-medium text-sm">
                              {item.classification}
                            </span>
                          </>
                        ) : item.classification === "Chưa đánh giá" ? (
                          <>
                            <XCircle className="h-4 w-4 text-yellow-600" />
                            <span className="text-yellow-600 font-medium text-sm">
                              {item.classification}
                            </span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-orange-600" />
                            <span className="text-orange-600 font-medium text-sm">
                              {item.classification}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div
                        className="relative inline-block"
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          data-project-id={item.id}
                          className="bg-[#337ab7] text-white px-2 py-1 rounded text-xs font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-1"
                          onClick={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            const rect = target.getBoundingClientRect();
                            const estimatedMenuHeight = 200;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const willOverflowBottom = spaceBelow < 250;
                            setMenuPlacement(
                              willOverflowBottom ? "top" : "bottom"
                            );
                            setButtonPosition({
                              left: rect.left,
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                            });
                            setOpenMenuProjectId(
                              openMenuProjectId === item.id ? null : item.id
                            );
                          }}
                        >
                          <span>Chọn</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        {openMenuProjectId === item.id &&
                          createPortal(
                            <div
                              className={
                                `fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[99999] ` +
                                (menuPlacement === "bottom"
                                  ? "mt-2"
                                  : "bottom-full mb-2")
                              }
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              style={
                                buttonPosition
                                  ? {
                                      left: `${buttonPosition.right - 200}px`,
                                      top:
                                        menuPlacement === "bottom"
                                          ? `${buttonPosition.bottom + 0}px`
                                          : `${buttonPosition.top - 180}px`,
                                      backgroundColor: "white",
                                      zIndex: 99999,
                                    }
                                  : {}
                              }
                            >
                              <div className="py-1 bg-white">
                                {[
                                  {
                                    label: "Chi tiết",
                                    Icon: Eye,
                                    colorClass: "text-blue-600",
                                  },
                                  {
                                    label: "Sửa",
                                    Icon: Pencil,
                                    colorClass: "text-amber-600",
                                  },
                                  {
                                    label: "Xóa",
                                    Icon: Trash2,
                                    colorClass: "text-red-600",
                                  },
                                ].map(({ label, Icon, colorClass }) =>
                                  label === "Chi tiết" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/incomplete-projects/detail/${item.id}`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Sửa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setOpenMenuProjectId(null);
                                        navigate(
                                          `/incomplete-projects/edit/${item.id}`
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : label === "Xóa" ? (
                                    <button
                                      key={label}
                                      onClick={() => {
                                        setDeleteProjectId(item.id);
                                        setShowDeleteConfirm(true);
                                        setOpenMenuProjectId(null);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                    >
                                      <Icon
                                        className={`h-4 w-4 ${colorClass}`}
                                      />
                                      <span>{label}</span>
                                    </button>
                                  ) : null
                                )}
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Xác nhận xóa
              </h3>
              <p className="text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa đề tài này không? Hành động này không
                thể hoàn tác.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default IncompleteProjects;
